// ABOUTME: Renames a business from its row in the Agents tree, through
// useMyBusinessStore.updateBusiness (PATCH /businesses/{id}).
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { isApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface RenameBusinessDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentName: string
  onSubmit: (name: string) => Promise<void>
}

export function RenameBusinessDialog({
  open,
  onOpenChange,
  currentName,
  onSubmit,
}: RenameBusinessDialogProps) {
  const [name, setName] = useState(currentName)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setName(currentName)
      setError(null)
    }
  }, [open, currentName])

  const trimmed = name.trim()
  const handleSave = async () => {
    if (!trimmed || trimmed === currentName) return
    setSaving(true)
    try {
      await onSubmit(trimmed)
      onOpenChange(false)
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Could not rename the business')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename business</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="rename-business-name">Business name</Label>
          <Input
            id="rename-business-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !saving && void handleSave()}
            data-testid="rename-business-input"
          />
          {error && (
            <p className="text-destructive text-sm" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={saving || !trimmed || trimmed === currentName}
            data-testid="rename-business-submit"
          >
            {saving && <Loader2 className="size-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
